import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

/**
 * Selecting blocks on the board.
 *
 * Three ways in, all of them the ones people already know from a file manager:
 * click to select one, shift or cmd-click to add, and shift/cmd-drag on empty
 * grid to sweep a rectangle. A plain drag on empty grid draws a slot instead
 * (decision 69). Once a set is selected, one drag moves all of it.
 *
 * The marquee is kept here rather than in the gesture machine because it never
 * changes any data — it is a lasso, and mixing it into the drag state machine
 * would mean every branch there has to ask whether it is really editing.
 */

export interface Marquee {
  left: number
  top: number
  width: number
  height: number
}

export interface SelectionApi {
  selected: Set<string>
  isSelected: (id: string) => boolean
  /** Click on a block. Additive when shift or cmd/ctrl is held. */
  toggle: (id: string, additive: boolean) => void
  select: (ids: string[]) => void
  clear: () => void
  /** Pointer-down on empty grid. Starts a sweep. */
  beginMarquee: (event: ReactPointerEvent) => void
  /** Screen-space rectangle to draw, or null. */
  marquee: Marquee | null
}

export function useSelection({
  /** Screen rectangle of every block, keyed by visit, read when a sweep ends. */
  rects,
}: {
  rects: React.RefObject<Map<string, HTMLElement>>
}): SelectionApi {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [marquee, setMarquee] = useState<Marquee | null>(null)

  const sweep = useRef<{
    pointerId: number
    startX: number
    startY: number
    additive: boolean
    armed: boolean
  } | null>(null)

  const toggle = useCallback((id: string, additive: boolean) => {
    setSelected((previous) => {
      if (!additive) {
        // Clicking an already-selected block without a modifier keeps the
        // selection, so a group drag does not collapse to one on grab.
        return previous.has(id) ? previous : new Set([id])
      }

      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const select = useCallback((ids: string[]) => setSelected(new Set(ids)), [])
  const clear = useCallback(() => setSelected(new Set()), [])

  const beginMarquee = useCallback((event: ReactPointerEvent) => {
    if (event.button !== 0) return

    sweep.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      additive: event.shiftKey || event.metaKey || event.ctrlKey,
      armed: false,
    }
  }, [])

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const active = sweep.current
      if (!active || event.pointerId !== active.pointerId) return

      const left = Math.min(active.startX, event.clientX)
      const top = Math.min(active.startY, event.clientY)
      const width = Math.abs(event.clientX - active.startX)
      const height = Math.abs(event.clientY - active.startY)

      if (!active.armed && width < 4 && height < 4) return

      active.armed = true
      setMarquee({ left, top, width, height })
    }

    const onPointerUp = (event: PointerEvent) => {
      const active = sweep.current
      if (!active || event.pointerId !== active.pointerId) return
      sweep.current = null

      if (!active.armed) {
        // A plain click on empty grid means "nothing selected".
        if (!active.additive) setSelected(new Set())
        setMarquee(null)
        return
      }

      const box = {
        left: Math.min(active.startX, event.clientX),
        right: Math.max(active.startX, event.clientX),
        top: Math.min(active.startY, event.clientY),
        bottom: Math.max(active.startY, event.clientY),
      }

      const caught: string[] = []
      for (const [id, element] of rects.current ?? []) {
        const rect = element.getBoundingClientRect()
        // Touched, not enclosed. Requiring full containment means a sweep down
        // a column misses every block it clipped.
        const hit =
          rect.left < box.right &&
          rect.right > box.left &&
          rect.top < box.bottom &&
          rect.bottom > box.top
        if (hit) caught.push(id)
      }

      setSelected((previous) =>
        active.additive ? new Set([...previous, ...caught]) : new Set(caught),
      )
      setMarquee(null)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [rects])

  return {
    selected,
    isSelected: (id) => selected.has(id),
    toggle,
    select,
    clear,
    beginMarquee,
    marquee,
  }
}
